import React, { useEffect, useState } from 'react';
import { Star } from 'lucide-react';
import { get, post } from '../../utils/api';
import { Button } from '../shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../shadcn/dialog';

const GOOGLE_REVIEW_URL = process.env.REACT_APP_GOOGLE_REVIEW_URL;

const ReviewPromptBanner = () => {
  const [shouldShow, setShouldShow] = useState(false);
  const [hovered, setHovered] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Modal state: null | 'happy' | 'unhappy' | 'thanks'
  const [modal, setModal] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const response = await get('/review/status', true);
        if (!cancelled) setShouldShow(Boolean(response?.shouldShowPrompt));
      } catch {
        // Silently no-op — banner is non-critical
      }
    };
    fetchStatus();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleStarClick = async (rating) => {
    if (submitting) return;
    setSubmitting(true);
    // Hide banner immediately — don't wait for response
    setShouldShow(false);
    try {
      const response = await post('/review/rate', { rating }, true);
      setModal(response?.redirectToGoogle ? 'happy' : 'unhappy');
    } catch {
      // If the API fails, still show the appropriate modal based on local rating
      setModal(rating >= 4 ? 'happy' : 'unhappy');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSnooze = async () => {
    setShouldShow(false);
    try {
      await post('/review/snooze', {}, true);
    } catch {
      // Non-fatal — banner is already hidden locally
    }
  };

  const handleSubmitFeedback = async (e) => {
    e.preventDefault();
    if (feedbackSubmitting) return;
    setFeedbackSubmitting(true);
    try {
      await post('/review/feedback', { feedback: feedback.trim() }, true);
    } catch {
      // Non-fatal — still close
    } finally {
      setFeedbackSubmitting(false);
      setModal('thanks');
    }
  };

  const closeModal = () => {
    setModal(null);
    setFeedback('');
  };

  const handleGoogleClick = () => {
    // Fire-and-forget — the user is about to navigate away in a new tab.
    post('/review/google-clicked', {}, true).catch(() => {
      // Non-fatal — analytics signal only.
    });
  };

  return (
    <>
      {shouldShow && (
        <div className="bg-[#F1EEF7] border border-[#5C3BD1]/20 rounded-lg p-4 md:p-5 mb-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <p className="text-[#2F1E6B] font-medium">
            How is your child getting on with Nova Learning? Rate your experience:
          </p>
          <div className="flex items-center gap-3">
            <div
              className="flex gap-1"
              onMouseLeave={() => setHovered(0)}
            >
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => handleStarClick(n)}
                  onMouseEnter={() => setHovered(n)}
                  disabled={submitting}
                  aria-label={`Rate ${n} ${n === 1 ? 'star' : 'stars'}`}
                  className="p-1 disabled:opacity-50"
                >
                  <Star
                    className={
                      n <= hovered
                        ? 'h-7 w-7 fill-[#F4B400] text-[#F4B400]'
                        : 'h-7 w-7 text-gray-400'
                    }
                  />
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={handleSnooze}
              disabled={submitting}
              className="text-sm text-gray-500 hover:text-[#2F1E6B] underline disabled:opacity-50"
            >
              Not now
            </button>
          </div>
        </div>
      )}

      <Dialog open={modal === 'happy'} onOpenChange={(open) => !open && closeModal()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>We're so glad to hear that!</DialogTitle>
            <DialogDescription>
              Help other families discover Nova Learning by sharing your experience on Google.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 mt-2">
            {GOOGLE_REVIEW_URL ? (
              <a
                href={GOOGLE_REVIEW_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleGoogleClick}
              >
                <Button className="w-full bg-[#5C3BD1] hover:bg-[#2F1E6B] text-white">
                  Leave a Google Review
                </Button>
              </a>
            ) : (
              <p className="text-sm text-gray-500">
                Google review link not configured.
              </p>
            )}
            <Button variant="outline" onClick={closeModal}>
              Maybe later
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={modal === 'unhappy'} onOpenChange={(open) => !open && closeModal()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>We're sorry to hear that.</DialogTitle>
            <DialogDescription>
              Help us understand what went wrong so we can do better.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitFeedback} className="mt-2">
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              maxLength={2000}
              rows={5}
              placeholder="Tell us what went wrong (optional)"
              className="w-full border border-gray-300 rounded-md p-3 text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#5C3BD1]"
            />
            <div className="flex gap-2 mt-3">
              <Button
                type="button"
                variant="outline"
                onClick={closeModal}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={feedbackSubmitting}
                className="flex-1 bg-[#5C3BD1] hover:bg-[#2F1E6B] text-white"
              >
                {feedbackSubmitting ? 'Submitting…' : 'Submit feedback'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={modal === 'thanks'} onOpenChange={(open) => !open && closeModal()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Thank you for letting us know.</DialogTitle>
            <DialogDescription>
              We'll look into this. Your feedback helps us improve.
            </DialogDescription>
          </DialogHeader>
          <Button onClick={closeModal} className="mt-2">
            Close
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReviewPromptBanner;
